import React, { useEffect, useState } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { motion } from 'framer-motion';
import api from '../utils/api';
import ResponsiveChart from '../components/ResponsiveChart';
import { transformCategoryToChart, transformSeriesToChart } from '../api/adapters/analyticsAdapter';

const AnalyticsReport: React.FC = () => {
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('7d');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>({ stats: {}, trends: { labels: [], data: [] }, channels: { labels: [], data: [] } });

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/analytics/report?period=${period}`);
      setReport(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [period]);

  const exportReport = async (format: 'pdf' | 'csv') => {
    try {
      const res = await api.get(`/analytics/report/export?format=${format}&period=${period}`, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-report-${period}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-white">Analytics Report</h1>
        <div className="flex gap-2">
          {(['7d', '30d', '90d'] as const).map((p) => (
            <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-2 rounded ${period === p ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300'}`}>{p}</button>
          ))}
          <button onClick={() => exportReport('csv')} className="px-3 py-2 bg-gray-800 text-gray-300 rounded">Export CSV</button>
          <button onClick={() => exportReport('pdf')} className="px-3 py-2 bg-gray-800 text-gray-300 rounded">Export PDF</button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-900/40 border border-red-700 text-red-200">{error}</div>
      )}
      {loading && (
        <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div></div>
      )}

      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div className="rounded-2xl p-6 bg-gray-800/80 border border-gray-700/50">
            <h3 className="text-white mb-3">Conversation Trends</h3>
            <div style={{ height: 300 }}>
              <ResponsiveChart ariaLabel="Trends">
                <Line data={transformSeriesToChart(report.trends, 'Conversations') as any} options={{ responsive: true, maintainAspectRatio: false }} />
              </ResponsiveChart>
            </div>
          </motion.div>
          <motion.div className="rounded-2xl p-6 bg-gray-800/80 border border-gray-700/50">
            <h3 className="text-white mb-3">Channel Distribution</h3>
            <div style={{ height: 300 }}>
              <ResponsiveChart ariaLabel="Channels">
                <Bar data={transformCategoryToChart(report.channels, 'Channels') as any} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
              </ResponsiveChart>
            </div>
          </motion.div>
          <motion.div className="rounded-2xl p-6 bg-gray-800/80 border border-gray-700/50 lg:col-span-2">
            <h3 className="text-white mb-3">Outcomes</h3>
            <div style={{ height: 300 }}>
              <ResponsiveChart ariaLabel="Outcomes">
                <Doughnut data={transformCategoryToChart({ labels: ['Completed', 'In Progress', 'Failed'], data: [report.stats?.completed || 0, report.stats?.inProgress || 0, report.stats?.failed || 0] }, 'Outcomes') as any} options={{ responsive: true, maintainAspectRatio: false }} />
              </ResponsiveChart>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsReport;


